<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  // Null while a fetch is in flight so stale counts are never shown.
  totalCount: {
    type: Number,
    default: null,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  // Self-scoped viewers only ever see their own calls, so the assignee filter
  // is meaningless for them — only admins get it.
  showAssignee: {
    type: Boolean,
    default: false,
  },
});

const activity = defineModel('activity', { type: String, default: null });
const assigneeId = defineModel('assigneeId', { type: Number, default: null });
const inboxId = defineModel('inboxId', { type: Number, default: null });

const { t } = useI18n();

const ACTIVITY_ICONS = {
  missed: 'i-lucide-phone-missed',
  no_reply: 'i-lucide-phone-outgoing',
  incoming: 'i-lucide-phone-incoming',
  outgoing: 'i-lucide-phone-outgoing',
  in_progress: 'i-lucide-phone-call',
};

const BASE_ACTIVITIES = ['missed', 'no_reply'];
const OTHER_ACTIVITIES = ['incoming', 'outgoing', 'in_progress'];

// A single open-menu identifier keeps the three dropdowns mutually exclusive:
// opening one closes the others without any cross-wiring.
const openMenu = ref(null); // 'activity' | 'assignee' | 'more' | null

const toggleMenu = name => {
  openMenu.value = openMenu.value === name ? null : name;
};

// Each dropdown wrapper closes itself on outside clicks. The guard keeps the
// other two wrappers (which the click is also outside of) from closing a
// menu the user is interacting with.
const closeOnOutside = name => {
  if (openMenu.value === name) openMenu.value = null;
};

const activityLabel = value => t(`CALLS_PAGE.FILTERS.${value.toUpperCase()}`);

const activeChipLabel = computed(() => {
  const label = activityLabel(activity.value);
  return props.totalCount === null ? label : `${label} (${props.totalCount})`;
});

const inactiveChips = computed(() =>
  BASE_ACTIVITIES.filter(value => value !== activity.value)
);

const otherActivityItems = computed(() =>
  OTHER_ACTIVITIES.map(value => ({
    label: activityLabel(value),
    value,
    action: 'filter',
    icon: ACTIVITY_ICONS[value],
    isSelected: activity.value === value,
  }))
);

const assigneeItems = computed(() => [
  {
    label: t('CALLS_PAGE.FILTERS.ALL_ASSIGNEES'),
    value: null,
    action: 'filter',
    isSelected: !assigneeId.value,
  },
  ...props.agents.map(agent => ({
    label: agent.name,
    value: agent.id,
    action: 'filter',
    thumbnail: { name: agent.name, src: agent.thumbnail },
    isSelected: assigneeId.value === agent.id,
  })),
]);

const moreFiltersSections = computed(() => [
  {
    title: t('CALLS_PAGE.FILTERS.INBOX'),
    items: [
      {
        label: t('CALLS_PAGE.FILTERS.ALL_INBOXES'),
        value: null,
        action: 'inbox',
        isSelected: !inboxId.value,
      },
      ...props.inboxes.map(inbox => ({
        label: inbox.name,
        value: inbox.id,
        action: 'inbox',
        isSelected: inboxId.value === inbox.id,
      })),
    ],
  },
]);

const selectedAssignee = computed(
  () => props.agents.find(agent => agent.id === assigneeId.value) || null
);

const selectedAssigneeLabel = computed(
  () => selectedAssignee.value?.name || t('CALLS_PAGE.FILTERS.ASSIGNEE')
);

const isOtherActivitySelected = computed(() =>
  OTHER_ACTIVITIES.includes(activity.value)
);

const hasMoreFilters = computed(() => Boolean(inboxId.value));

const setActivity = value => {
  openMenu.value = null;
  activity.value = value;
};

const setAssignee = ({ value }) => {
  openMenu.value = null;
  assigneeId.value = value;
};

const applyMoreFilter = ({ action, value }) => {
  openMenu.value = null;
  if (action === 'inbox') inboxId.value = value;
};
</script>

<template>
  <div
    class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-auth-border bg-auth-surface p-3 dark:border-auth-border-dark dark:bg-auth-surface-dark [&_button]:transition-colors [&_button]:focus-visible:!outline [&_button]:focus-visible:!outline-2 [&_button]:focus-visible:!outline-offset-2 [&_button]:focus-visible:!outline-auth-action dark:[&_button]:focus-visible:!outline-auth-accent"
  >
    <div class="flex flex-wrap items-center gap-2">
      <span
        v-if="!activity"
        class="shrink-0 text-sm font-medium text-auth-muted dark:text-auth-muted-dark"
      >
        {{
          totalCount === null
            ? t('CALLS_PAGE.ALL_CALLS')
            : t('CALLS_PAGE.ALL_CALLS_COUNT', { count: totalCount })
        }}
      </span>
      <Button
        v-else
        variant="outline"
        color="blue"
        size="sm"
        :icon="ACTIVITY_ICONS[activity]"
        class="shrink-0 !h-8 !rounded-lg !border-auth-action/40 !bg-auth-action/10 !px-2.5 !text-auth-action hover:!bg-auth-action/20 dark:!border-auth-accent/40 dark:!bg-auth-accent/10 dark:!text-auth-accent dark:hover:!bg-auth-accent/20"
        @click="setActivity(null)"
      >
        {{ activeChipLabel }}
        <Icon icon="i-lucide-x" />
      </Button>
      <div
        class="mx-1 h-4 w-px shrink-0 bg-auth-border dark:bg-auth-border-dark"
      />
      <Button
        v-for="chip in inactiveChips"
        :key="chip"
        variant="outline"
        color="slate"
        size="sm"
        :icon="ACTIVITY_ICONS[chip]"
        :label="activityLabel(chip)"
        class="shrink-0 !h-8 !rounded-lg !border-auth-border !bg-auth-soft !px-2.5 !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark"
        @click="setActivity(chip)"
      />
      <OnClickOutside
        class="relative shrink-0"
        @trigger="closeOnOutside('activity')"
      >
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-phone"
          class="!h-8 !rounded-lg !px-2.5"
          :class="
            isOtherActivitySelected
              ? '!border-auth-action/40 !bg-auth-action/10 !text-auth-action hover:!bg-auth-action/20 dark:!border-auth-accent/40 dark:!bg-auth-accent/10 dark:!text-auth-accent dark:hover:!bg-auth-accent/20'
              : '!border-auth-border !bg-auth-soft !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark'
          "
          @click="toggleMenu('activity')"
        >
          {{ t('CALLS_PAGE.FILTERS.OTHER_ACTIVITY') }}
          <Icon icon="i-lucide-chevron-down" />
        </Button>
        <DropdownMenu
          v-if="openMenu === 'activity'"
          :menu-items="otherActivityItems"
          class="start-0 top-full mt-2 w-44 !border-auth-border !bg-auth-surface !text-auth-text shadow-lg shadow-auth-brand/10 dark:!border-auth-border-dark dark:!bg-auth-surface-dark dark:!text-auth-text-dark"
          @action="setActivity($event.value)"
        />
      </OnClickOutside>
    </div>
    <div class="flex shrink-0 flex-wrap items-center gap-2">
      <OnClickOutside
        v-if="showAssignee"
        class="relative"
        @trigger="closeOnOutside('assignee')"
      >
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-woot-empty-assignee"
          class="max-w-52 !h-8 !rounded-lg !px-2.5"
          :class="
            assigneeId
              ? '!border-auth-action/40 !bg-auth-action/10 !text-auth-action hover:!bg-auth-action/20 dark:!border-auth-accent/40 dark:!bg-auth-accent/10 dark:!text-auth-accent dark:hover:!bg-auth-accent/20'
              : '!border-auth-border !bg-auth-soft !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark'
          "
          @click="toggleMenu('assignee')"
        >
          <template v-if="selectedAssignee" #icon>
            <Avatar
              :src="selectedAssignee.thumbnail"
              :name="selectedAssignee.name"
              :size="16"
              rounded-full
            />
          </template>
          <span class="truncate">{{ selectedAssigneeLabel }}</span>
          <Icon icon="i-lucide-chevron-down" class="shrink-0" />
        </Button>
        <DropdownMenu
          v-if="openMenu === 'assignee'"
          :menu-items="assigneeItems"
          show-search
          class="end-0 top-full mt-2 max-h-72 w-56 !border-auth-border !bg-auth-surface !text-auth-text shadow-lg shadow-auth-brand/10 dark:!border-auth-border-dark dark:!bg-auth-surface-dark dark:!text-auth-text-dark"
          @action="setAssignee"
        />
      </OnClickOutside>
      <OnClickOutside class="relative" @trigger="closeOnOutside('more')">
        <Button
          variant="outline"
          size="sm"
          icon="i-lucide-list-filter"
          class="!h-8 !rounded-lg !px-2.5"
          :color="hasMoreFilters ? 'blue' : 'slate'"
          :class="
            hasMoreFilters
              ? '!border-auth-action/40 !bg-auth-action/10 !text-auth-action hover:!bg-auth-action/20 dark:!border-auth-accent/40 dark:!bg-auth-accent/10 dark:!text-auth-accent dark:hover:!bg-auth-accent/20'
              : '!border-auth-border !bg-auth-soft !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark'
          "
          @click="toggleMenu('more')"
        >
          {{ t('CALLS_PAGE.FILTERS.MORE_FILTERS') }}
          <Icon icon="i-lucide-chevron-down" />
        </Button>
        <DropdownMenu
          v-if="openMenu === 'more'"
          :menu-sections="moreFiltersSections"
          class="end-0 top-full mt-2 max-h-80 w-56 !border-auth-border !bg-auth-surface !text-auth-text shadow-lg shadow-auth-brand/10 dark:!border-auth-border-dark dark:!bg-auth-surface-dark dark:!text-auth-text-dark"
          @action="applyMoreFilter"
        />
      </OnClickOutside>
    </div>
  </div>
</template>
